import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Download,
  Search,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  FileText,
  X
} from 'lucide-react';
import { statisticsApi } from '@/api/raynetClient';

// Types for drill-down data
export interface DrillDownData {
  type: 'status' | 'representative' | 'monthly' | 'design' | 'cooperation' | 'competition' | 'regional';
  title: string;
  description?: string;
  value: string | number;
  filters?: Record<string, any>;
}

interface DrillDownItem {
  id: string;
  [key: string]: any;
}

interface DrillDownColumn {
  key: string;
  title: string;
  sortable?: boolean;
  filterable?: boolean;
  render?: (value: any, item: DrillDownItem) => React.ReactNode;
}

interface StatisticsDrillDownModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: DrillDownData | null;
}

// Column configurations for different drill-down types
const getColumnsForType = (type: DrillDownData['type']): DrillDownColumn[] => {
  switch (type) {
    case 'status':
      return [
        { key: 'company_name', title: 'Firma', sortable: true, filterable: true },
        { key: 'contact_person', title: 'Kontaktní osoba', sortable: true },
        { key: 'raynet_user', title: 'Obchodník', sortable: true, filterable: true },
        { key: 'created_at', title: 'Vytvořeno', sortable: true, render: (value) => new Date(value).toLocaleDateString('cs-CZ') },
        { key: 'updated_at', title: 'Aktualizováno', sortable: true, render: (value) => new Date(value).toLocaleDateString('cs-CZ') },
        { 
          key: 'status', 
          title: 'Stav', 
          render: (value) => {
            const statusColors: Record<string, string> = {
              draft: 'bg-gray-500',
              submitted: 'bg-orange-500',
              synced: 'bg-green-500',
              error: 'bg-red-500'
            };
            const statusLabels: Record<string, string> = {
              draft: 'Koncept',
              submitted: 'Odeslaný',
              synced: 'Synchronizován',
              error: 'Chyba'
            };
            return <Badge className={statusColors[value] || 'bg-gray-500'}>{statusLabels[value] || value}</Badge>;
          }
        }
      ];
    
    case 'representative':
      return [
        { key: 'company_name', title: 'Firma', sortable: true, filterable: true },
        { key: 'contact_person', title: 'Kontaktní osoba', sortable: true },
        { key: 'created_at', title: 'Vytvořeno', sortable: true, render: (value) => new Date(value).toLocaleDateString('cs-CZ') },
        { key: 'completion_time', title: 'Doba vyplnění (dny)', sortable: true, render: (value) => value ? `${value} dní` : 'N/A' },
        { 
          key: 'status', 
          title: 'Stav', 
          render: (value) => {
            const statusColors: Record<string, string> = {
              draft: 'bg-gray-500',
              submitted: 'bg-orange-500', 
              synced: 'bg-green-500',
              error: 'bg-red-500'
            };
            const statusLabels: Record<string, string> = {
              draft: 'Koncept',
              submitted: 'Odeslaný',
              synced: 'Synchronizován',
              error: 'Chyba'
            };
            return <Badge className={statusColors[value] || 'bg-gray-500'}>{statusLabels[value] || value}</Badge>;
          }
        }
      ];
    
    case 'monthly':
      return [
        { key: 'day', title: 'Den', sortable: true },
        { key: 'count', title: 'Počet formulářů', sortable: true },
        { key: 'completed', title: 'Dokončeno', sortable: true },
        { key: 'completion_rate', title: 'Míra dokončení', sortable: true, render: (value) => `${(value * 100).toFixed(1)}%` }
      ];
    
    case 'design':
      return [
        { key: 'company_name', title: 'Firma', sortable: true, filterable: true },
        { key: 'contact_person', title: 'Kontakt', sortable: true },
        { key: 'design_impression', title: 'Hodnocení designu', sortable: true, filterable: true },
        { key: 'design_comments', title: 'Komentáře', render: (value) => value || 'Bez komentáře' },
        { key: 'created_at', title: 'Datum', sortable: true, render: (value) => new Date(value).toLocaleDateString('cs-CZ') }
      ];

    case 'cooperation':
      return [
        { key: 'company_name', title: 'Firma', sortable: true, filterable: true },
        { key: 'contact_person', title: 'Kontakt', sortable: true },
        { key: 'cooperation_interest', title: 'Zájem o spolupráci', sortable: true, filterable: true },
        { key: 'cooperation_comments', title: 'Komentáře', render: (value) => value || 'Bez komentáře' },
        { key: 'created_at', title: 'Datum', sortable: true, render: (value) => new Date(value).toLocaleDateString('cs-CZ') }
      ];
    
    case 'competition':
      return [
        { key: 'company_name', title: 'Firma', sortable: true, filterable: true },
        { key: 'contact_person', title: 'Kontakt', sortable: true },
        { key: 'competitor_brands', title: 'Značky konkurence', render: (value) => Array.isArray(value) ? value.join(', ') : value },
        { key: 'market_analysis', title: 'Analýza trhu', render: (value) => value || 'Bez analýzy' },
        { key: 'created_at', title: 'Datum', sortable: true, render: (value) => new Date(value).toLocaleDateString('cs-CZ') }
      ];
    
    case 'regional':
      return [
        { key: 'company_name', title: 'Firma', sortable: true, filterable: true },
        { key: 'region', title: 'Region', sortable: true, filterable: true },
        { key: 'regional_potential', title: 'Potenciál regionu', sortable: true },
        { key: 'expected_volume', title: 'Očekávaný objem', sortable: true },
        { key: 'created_at', title: 'Datum', sortable: true, render: (value) => new Date(value).toLocaleDateString('cs-CZ') }
      ];
    
    default:
      return [];
  }
};

export function StatisticsDrillDownModal({ isOpen, onClose, data }: StatisticsDrillDownModalProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortColumn, setSortColumn] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});

  // Fetch drill-down data
  const { data: drillDownItems, isLoading, error } = useQuery({
    queryKey: ['drill-down', data?.type, data?.value, data?.filters],
    queryFn: () => {
      if (!data) return Promise.resolve([]);
      return statisticsApi.getDrillDownData(data.type, data.value, data.filters);
    },
    enabled: isOpen && !!data,
  });

  const columns = useMemo(() => {
    return data ? getColumnsForType(data.type) : [];
  }, [data?.type]);

  // Filter and sort data
  const processedData = useMemo(() => {
    if (!drillDownItems) return [];

    let filtered = drillDownItems;

    // Apply search
    if (searchTerm) {
      filtered = filtered.filter((item: DrillDownItem) =>
        Object.values(item).some(value =>
          String(value).toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }

    // Apply column filters
    Object.entries(columnFilters).forEach(([column, filterValue]) => {
      if (filterValue) {
        filtered = filtered.filter((item: DrillDownItem) =>
          String(item[column]).toLowerCase().includes(filterValue.toLowerCase())
        );
      }
    });

    // Apply sorting
    if (sortColumn) {
      filtered.sort((a: DrillDownItem, b: DrillDownItem) => {
        const aVal = a[sortColumn];
        const bVal = b[sortColumn];
        
        if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  }, [drillDownItems, searchTerm, sortColumn, sortDirection, columnFilters]);

  const handleSort = (columnKey: string) => {
    if (sortColumn === columnKey) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(columnKey);
      setSortDirection('asc');
    }
  };

  const handleColumnFilter = (columnKey: string, value: string) => {
    setColumnFilters(prev => ({
      ...prev,
      [columnKey]: value
    }));
  };

  const clearFilters = () => {
    setSearchTerm('');
    setColumnFilters({});
    setSortColumn('');
    setSortDirection('asc');
  };

  const handleExport = async () => {
    if (!data || !processedData.length) return;
    
    try {
      const csvContent = [
        columns.map(col => col.title).join(','),
        ...processedData.map((item: DrillDownItem) =>
          columns.map(col => {
            const value = item[col.key];
            return `"${String(value).replace(/"/g, '""')}"`;
          }).join(',')
        )
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `${data.type}_drill_down_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Chyba při exportu:', error);
    }
  };

  if (!data) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            {data.title}
          </DialogTitle>
          {data.description && (
            <DialogDescription>
              {data.description}
            </DialogDescription>
          )}
        </DialogHeader>

        {/* Controls */}
        <div className="flex flex-col gap-4 border-b pb-4">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-2 flex-1 min-w-[200px]">
              <Search className="h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Hledat v datech..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="flex-1"
              />
            </div>
            <Button variant="outline" onClick={clearFilters}>
              <X className="h-4 w-4 mr-2" />
              Vymazat filtry
            </Button>
            <Button variant="outline" onClick={handleExport} disabled={!processedData.length}>
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
          </div>

          {/* Column Filters */}
          <div className="flex gap-2 flex-wrap">
            {columns.filter(col => col.filterable).map(column => (
              <div key={column.key} className="flex items-center gap-1">
                <Label className="text-xs text-muted-foreground">{column.title}:</Label>
                <Input
                  placeholder={`Filter ${column.title.toLowerCase()}...`}
                  value={columnFilters[column.key] || ''}
                  onChange={(e) => handleColumnFilter(column.key, e.target.value)}
                  className="w-32 h-8 text-xs"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 py-2">
          <Card className="p-3">
            <CardContent className="p-0">
              <div className="text-sm text-muted-foreground">Celkem záznamů</div>
              <div className="text-lg font-bold">{drillDownItems?.length || 0}</div>
            </CardContent>
          </Card>
          <Card className="p-3">
            <CardContent className="p-0">
              <div className="text-sm text-muted-foreground">Zobrazeno</div>
              <div className="text-lg font-bold">{processedData.length}</div>
            </CardContent>
          </Card>
          <Card className="p-3">
            <CardContent className="p-0">
              <div className="text-sm text-muted-foreground">Hodnota</div>
              <div className="text-lg font-bold">{data.value}</div>
            </CardContent>
          </Card>
          <Card className="p-3">
            <CardContent className="p-0">
              <div className="text-sm text-muted-foreground">Typ analýzy</div>
              <div className="text-lg font-bold capitalize">{data.type}</div>
            </CardContent>
          </Card>
        </div>

        {/* Data Table */}
        <div className="flex-1 overflow-auto">
          {isLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : error ? (
            <div className="text-center p-8">
              <p className="text-red-500">Chyba při načítání dat: {error.message}</p>
            </div>
          ) : processedData.length === 0 ? (
            <div className="text-center p-8">
              <p className="text-muted-foreground">Žádná data k zobrazení</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  {columns.map((column) => (
                    <TableHead key={column.key} className="text-left">
                      {column.sortable ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleSort(column.key)}
                          className="h-auto p-0 font-medium"
                        >
                          {column.title}
                          {sortColumn === column.key && (
                            sortDirection === 'asc' ? 
                              <ArrowUp className="ml-1 h-3 w-3" /> : 
                              <ArrowDown className="ml-1 h-3 w-3" />
                          )}
                          {sortColumn !== column.key && <ArrowUpDown className="ml-1 h-3 w-3 opacity-50" />}
                        </Button>
                      ) : (
                        <span className="font-medium">{column.title}</span>
                      )}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {processedData.map((item: DrillDownItem, index: number) => (
                  <TableRow key={item.id || index}>
                    {columns.map((column) => (
                      <TableCell key={column.key}>
                        {column.render 
                          ? column.render(item[column.key], item)
                          : item[column.key] || 'N/A'
                        }
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}